'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  HeartHandshake,
  Megaphone,
  Pause,
  Play,
  Rocket,
  Video
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import { trackCta } from '~/lib/track-cta';

const ROTATE_INTERVAL = 4000;

const cases = [
  {
    icon: Rocket,
    title: 'SaaS',
    tagline: 'Turn features into product demos',
    desc: 'Record your product, and let AI generate polished demos, onboarding tours, and feature walkthroughs. Help prospects understand your product faster without a dedicated video team.',
    features: [
      'Product walkthroughs',
      'Feature announcements',
      'Onboarding tours'
    ],
    image: '/assets/homepage/usecases/startup.jpg',
    stat: '3x',
    statLabel: 'Faster onboarding'
  },
  {
    icon: Megaphone,
    title: 'Marketing Teams',
    tagline: 'Scale video production effortlessly',
    desc: 'Produce feature announcements, walkthrough campaigns, and social media clips at scale. Keep your content pipeline full without bottlenecking on video editors.',
    features: ['Batch creation', 'Brand consistency', 'Social-ready formats'],
    image: '/assets/homepage/usecases/marketing.jpg',
    stat: '5x',
    statLabel: 'More content output'
  },
  {
    icon: HeartHandshake,
    title: 'Customer Success',
    tagline: 'Instant training & support videos',
    desc: 'Create training materials, help center videos, and onboarding guides instantly. Reduce support tickets and empower customers with clear visual instructions.',
    features: [
      'Help center ready',
      'Auto-update videos',
      'Multi-language support'
    ],
    image: '/assets/homepage/usecases/customer-success.jpg',
    stat: '40%',
    statLabel: 'Fewer support tickets'
  },
  {
    icon: BookOpen,
    title: 'Technical Writers',
    tagline: 'Docs to video in one click',
    desc: 'Transform documentation into visual step-by-step video guides. Bridge the gap between written docs and visual learning with zero extra effort.',
    features: ['Doc-to-video convert', 'Step detection', 'Version control'],
    image: '/assets/homepage/usecases/tech-writer.jpg',
    stat: '2min',
    statLabel: 'Avg. creation time'
  },
  {
    icon: GraduationCap,
    title: 'Educators',
    tagline: 'Create engaging course content',
    desc: 'Build compelling lesson videos and course modules with natural AI narration. Keep students engaged with professional visuals without spending hours in editing software.',
    features: [
      'Multiple AI voice styles',
      'Step-by-step flow',
      'Quiz-ready clips'
    ],
    image: '/assets/homepage/usecases/educator.jpg',
    stat: '85%',
    statLabel: 'More engagement'
  },
  {
    icon: Video,
    title: 'Content Creators',
    tagline: 'Turn recordings into polished videos',
    desc: 'Transform raw screen recordings into professional tutorial videos with AI-generated voiceover, captions, and edits — ready for YouTube and social media in minutes.',
    features: [
      'Auto narration & sync',
      'Caption generation',
      'Multi-format export'
    ],
    image: '/assets/homepage/usecases/content-creator.jpg',
    stat: '10x',
    statLabel: 'Faster production'
  }
];

export default function UseCases() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const current = cases[active];
  const Icon = current.icon;

  const goNext = useCallback(() => {
    setActive((prev) => (prev + 1) % cases.length);
    setProgress(0);
  }, []);

  const goPrev = useCallback(() => {
    setActive((prev) => (prev - 1 + cases.length) % cases.length);
    setProgress(0);
  }, []);

  useEffect(() => {
    if (paused) return;
    const tick = 50;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + (tick / ROTATE_INTERVAL) * 100;
        if (next >= 100) {
          goNext();
          return 0;
        }
        return next;
      });
    }, tick);
    return () => clearInterval(interval);
  }, [paused, goNext]);

  const selectTab = (i: number) => {
    setActive(i);
    setProgress(0);
  };

  return (
    <section className="py-14 sm:py-20 md:py-24 bg-background">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center rounded-full border border-videomule-green/20 px-3 py-1 text-xs font-medium text-videomule-green mb-4 bg-videomule-light-green dark:bg-videomule-green/10">
            Use Cases
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
            Built for Every Team
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-sm sm:text-base md:text-lg">
            No matter your role, VideoMule adapts to your workflow.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Tabs with progress */}
          <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-10 items-center">
            {cases.map((item, i) => {
              const TabIcon = item.icon;
              const isActive = i === active;
              return (
                <button
                  key={i}
                  onClick={() => selectTab(i)}
                  className={`relative flex items-center gap-1.5 sm:gap-2 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium transition-all duration-300 cursor-pointer overflow-hidden ${
                    isActive
                      ? 'bg-videomule-green text-white shadow-lg'
                      : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-videomule-green/30'
                  }`}
                >
                  {/* Progress bar inside active tab */}
                  {isActive && !paused && (
                    <div
                      className="absolute bottom-0 left-0 h-[3px] bg-white/40 rounded-full transition-none"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                  <TabIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{item.title}</span>
                </button>
              );
            })}

            {/* Pause / Play toggle */}
            <button
              onClick={() => setPaused(!paused)}
              className="ml-1 sm:ml-2 w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-videomule-green/30 transition-colors cursor-pointer"
              aria-label={paused ? 'Resume auto-rotate' : 'Pause auto-rotate'}
            >
              {paused ? (
                <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              ) : (
                <Pause className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              )}
            </button>
          </div>

          {/* Content panel with prev/next arrows */}
          <div className="relative">
            {/* Prev arrow */}
            <button
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 hidden md:flex w-10 h-10 rounded-full border border-border bg-card shadow-md items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors cursor-pointer"
              aria-label="Previous use case"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Next arrow */}
            <button
              onClick={() => {
                goNext();
                setPaused(true);
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 hidden md:flex w-10 h-10 rounded-full border border-border bg-card shadow-md items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors cursor-pointer"
              aria-label="Next use case"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="rounded-2xl sm:rounded-3xl border border-border bg-card overflow-hidden shadow-lg"
              >
                <div className="grid md:grid-cols-5 md:min-h-[460px]">
                  {/* Left: Image — 2 cols */}
                  <div className="md:col-span-2 relative overflow-hidden group min-h-[220px] sm:min-h-[280px] md:min-h-full">
                    <motion.img
                      key={current.image}
                      src={current.image}
                      alt={current.title}
                      initial={{ scale: 1.1, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.6 }}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />

                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-foreground/70 via-foreground/20 to-transparent md:bg-linear-to-r md:from-transparent md:via-transparent md:to-card" />

                    {/* Stat badge on image */}
                    <div className="absolute top-3 left-3 sm:top-5 sm:left-5">
                      <div className="rounded-xl sm:rounded-2xl bg-card/90 backdrop-blur-md border border-border px-3 py-2 sm:px-4 sm:py-3 shadow-lg">
                        <p className="text-xl sm:text-2xl font-black text-videomule-green leading-none">
                          {current.stat}
                        </p>
                        <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium mt-0.5">
                          {current.statLabel}
                        </p>
                      </div>
                    </div>

                    {/* Name badge on image — mobile only */}
                    <div className="absolute bottom-3 left-3 sm:bottom-5 sm:left-5 md:hidden">
                      <span className="inline-flex items-center gap-2 rounded-full bg-videomule-green/90 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold shadow-lg">
                        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        {current.title}
                      </span>
                    </div>
                  </div>

                  {/* Right: Content — 3 cols */}
                  <div className="md:col-span-3 p-5 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-videomule-green/10 flex items-center justify-center">
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-videomule-green" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground leading-tight">
                          {current.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-videomule-green font-medium">
                          {current.tagline}
                        </p>
                      </div>
                    </div>

                    <p className="text-muted-foreground leading-relaxed mb-6 sm:mb-8 max-w-lg text-sm sm:text-[15px]">
                      {current.desc}
                    </p>

                    {/* Feature checklist */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-6 sm:mb-8">
                      {current.features.map((feat, j) => (
                        <motion.div
                          key={feat}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: j * 0.1 }}
                          className="flex items-center gap-2 sm:gap-2.5 rounded-lg sm:rounded-xl bg-muted/50 border border-border/50 px-3 py-2.5 sm:px-4 sm:py-3"
                        >
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-videomule-green/15 flex items-center justify-center shrink-0">
                            <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-videomule-green" />
                          </div>
                          <span className="text-xs sm:text-sm font-medium text-foreground">
                            {feat}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    <Button
                      size="lg"
                      href={routes.dashboard.auth.SignUp}
                      onClick={() => trackCta('cta_signup_clicked', { surface: 'homepage_use_cases' })}
                      className="group w-fit text-base font-semibold gap-2 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Start Free Trial
                      <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom dots indicator */}
          <div className="flex justify-center gap-2 mt-5 sm:mt-6">
            {cases.map((_, i) => (
              <button
                key={i}
                onClick={() => selectTab(i)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === active
                    ? 'w-8 bg-videomule-green'
                    : 'w-2 bg-border hover:bg-muted-foreground/40'
                }`}
                aria-label={`Go to use case ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
